/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component } from 'react';
import propTypes from 'prop-types';
import flow from 'lodash/flow';
import { findDOMNode } from 'react-dom';
import { DragSource, DropTarget } from 'react-dnd';

import { makeId } from 'utils/selectors';

import styles from './Card.css';

// based on react-dnd sortable example
// https://react-dnd.github.io/react-dnd/examples-sortable-simple.html

// we only use one item type
const CARD = 'card';

// drag source logic
const cardSource = {
  /**
   * This function returns index if item which is being dragged
   * @param {object} props - props object for each item
   * @returns {object} returns an object of index in list
   */
  beginDrag(props) {
    return {
      index: props.index
    };
  },

  /**
   * This function calls the movecard prop when we finish dragging
   * movecard will make the page re-render with the final component list.
   * @param {object} props - props object for each item
   * @param {object} monitor - props object for each item
   */
  endDrag(props, monitor) {
    const dropIndex = monitor.getItem().index;
    const dragIndex = props.index;

    // we drag from the dragIndex and drop the card at the dropIndex.
    props.onMoveCard(dragIndex, dropIndex);
  }
};
const ds = DragSource(CARD, cardSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging()
}));

// drop target logic
const cardTarget = {
  /**
   * This function is executed at the time of item hover
   * @param {object} props - props object for each item
   * @param {object} monitor - item being dragged.
   * @param {object} component - item being hovered upon
   */
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    // Determine rectangle on screen
    // eslint-disable-next-line react/no-find-dom-node
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return;
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return;
    }

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    // eslint-disable-next-line no-param-reassign
    monitor.getItem().index = hoverIndex;
  }
};
const dt = DropTarget(CARD, cardTarget, connect => ({
  connectDropTarget: connect.dropTarget()
}));

/**
 * Component/item from draggable list
 */
export class CardBase extends Component {
  static propTypes = {
    children: propTypes.node,
    className: propTypes.string,
    connectDragSource: propTypes.func.isRequired,
    connectDropTarget: propTypes.func.isRequired,
    componentHoverEnter: propTypes.func.isRequired,
    componentHoverLeave: propTypes.func.isRequired,
    contentLinkDisabled: propTypes.bool,
    handleComponentSelection: propTypes.func.isRequired,
    index: propTypes.number.isRequired,
    isDragging: propTypes.bool.isRequired,
    isReadonly: propTypes.bool,
    selected: propTypes.bool,
    hovering: propTypes.bool,
    // eslint-disable-next-line react/no-unused-prop-types
    onMoveCard: propTypes.func.isRequired,
    previewMetadata: propTypes.object
  };

  static defaultProps = {
    children: null,
    className: '',
    contentLinkDisabled: true,
    isReadonly: false,
    selected: false,
    hovering: false,
    previewMetadata: {}
  };

  render() {
    const {
      className,
      children,
      connectDragSource,
      connectDropTarget,
      componentHoverEnter,
      componentHoverLeave,
      contentLinkDisabled,
      handleComponentSelection,
      hovering,
      index,
      isDragging,
      isReadonly,
      selected,
      previewMetadata: { isPreviewingOnMWP }
    } = this.props;

    const opacity = isDragging ? 0 : 1;

    const hoverOrSelectedStyle =
      // eslint-disable-next-line no-nested-ternary
      selected ? styles.selectedCard : hovering ? styles.hovering : styles.card;

    const card = (
      <div
        className={`${className} ${hoverOrSelectedStyle} d-flex flex-row justify-content-between`}
        style={{ opacity }}
        data-item="card"
        data-test-id={makeId({
          index,
          component: this.constructor.name
        })}
        onClick={() => {
          if (isPreviewingOnMWP && contentLinkDisabled) {
            return handleComponentSelection({
              Id: children.key,
              variantIds: children.props.variantids,
              childComponentIds: children.props.childids
            });
          }
          return {};
        }}
        onMouseEnter={() => {
          if (isPreviewingOnMWP && contentLinkDisabled) {
            return componentHoverEnter({
              Id: children.key,
              variantIds: children.props.variantids,
              childComponentIds: children.props.childids
            });
          }
          return () => {};
        }}
        onMouseLeave={() => {
          if (isPreviewingOnMWP && contentLinkDisabled) {
            return componentHoverLeave();
          }
          return () => {};
        }}
      >
        <span className="w-100">{children}</span>
      </div>
    );

    return isReadonly ? card : connectDragSource(connectDropTarget(card));
  }
}

export default flow(dt, ds)(CardBase);
